import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, Plus, Images, X, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { pickAndUpload } from "@/lib/upload";
import { colors } from "@/lib/theme";
import { photoDate } from "@/lib/date";
import { Avatar, Spinner } from "@/components/peeps/ui";
import { mediaIdFromUrl } from "@/lib/media";

export function PhotoGallery({ mode, groupId, eventId, themeColor, themeOn, title, subtitle, onBack }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [viewerIndex, setViewerIndex] = useState(null);

  const canUpload = mode !== "global";

  const load = useCallback(async () => {
    try {
      let data = [];
      if (mode === "global") data = await api.myPhotos();
      else if (mode === "event") data = await api.eventPhotos(String(eventId));
      else data = await api.groupPhotos(String(groupId));
      setPhotos(data);
    } catch (e) {
      toast(e.message || "Couldn't load photos");
    } finally {
      setLoading(false);
    }
  }, [mode, groupId, eventId]);

  useEffect(() => { load(); }, [load]);

  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const onUpload = async () => {
    if (!canUpload || uploading || !groupId) return;
    setUploading(true);
    try {
      const res = await pickAndUpload({ multiple: true });
      if (res && res.error) { toast(res.error); return; }
      const ids = (res || []).map(mediaIdFromUrl).filter(Boolean);
      if (ids.length === 0) return;
      await api.addPhotos(String(groupId), ids, eventId);
      toast(`Added ${ids.length} photo${ids.length === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDownload = async () => {
    const sel = photos.filter((p) => selected.has(p.id));
    if (sel.length === 0 || downloading) return;
    setDownloading(true);
    try {
      for (const p of sel) {
        const url = mediaUrl(p.url);
        const resp = await fetch(url);
        const blob = await resp.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `peeps-${p.id}.jpg`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
      }
      toast(`Saved ${sel.length} to your device`);
      exitSelect();
    } finally {
      setDownloading(false);
    }
  };

  const onDeleteViewer = async () => {
    if (viewerIndex === null) return;
    const p = photos[viewerIndex];
    if (!p) return;
    try {
      await api.deletePhoto(p.id);
      setViewerIndex(null);
      toast("Photo removed");
      await load();
    } catch (e) {
      toast(e.message || "Couldn't remove");
    }
  };

  const viewer = viewerIndex !== null ? photos[viewerIndex] : null;

  return (
    <div className="min-h-screen" style={{ background: colors.surface }}>
      <div className="flex items-center gap-2 px-4 py-3 sticky top-0 z-10" style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
        <button data-testid="gallery-back" onClick={onBack} className="p-1.5"><ChevronLeft size={22} color={colors.onSurface} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-bold text-[19px] truncate" style={{ color: colors.onSurface }}>{title}</h1>
          {!!subtitle && <p className="font-display text-[12.5px] truncate" style={{ color: colors.onSurfaceTertiary }}>{subtitle}</p>}
        </div>
        {photos.length > 0 && (
          <button data-testid="gallery-select-toggle" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} className="p-1.5 font-display font-bold text-sm" style={{ color: themeColor }}>
            {selectMode ? "Cancel" : "Select"}
          </button>
        )}
        {canUpload && !selectMode && (
          <button data-testid="gallery-upload" onClick={onUpload} disabled={uploading} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: themeColor }}>
            {uploading ? <Spinner style={{ width: 16, height: 16, borderTopColor: themeOn }} /> : <Plus size={20} color={themeOn} />}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center pt-24"><Spinner /></div>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center pt-24 px-6 text-center gap-2">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2" style={{ background: themeColor + "22" }}>
            <Images size={28} color={themeColor} />
          </div>
          <h2 className="font-display font-bold text-lg" style={{ color: colors.onSurface }}>No photos yet</h2>
          <p className="font-display text-[13.5px]" style={{ color: colors.onSurfaceTertiary }}>
            {canUpload ? "Tap + to share the first memories." : "Photos your groups share will show up here."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1 p-4 max-w-5xl mx-auto pb-28">
          {photos.map((item, index) => {
            const isSel = selected.has(item.id);
            return (
              <div
                key={item.id}
                data-testid={`photo-${item.id}`}
                onClick={() => (selectMode ? toggleSelect(item.id) : setViewerIndex(index))}
                className="relative rounded-md overflow-hidden cursor-pointer animate-fade-in"
                style={{ aspectRatio: "1 / 1", background: colors.surfaceSecondary }}
              >
                <img src={mediaUrl(item.url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                {selectMode && (
                  <div className="absolute inset-0 flex items-start justify-end p-1.5" style={{ background: isSel ? "rgba(21,22,30,0.35)" : "transparent" }}>
                    <span className="w-[22px] h-[22px] rounded-full flex items-center justify-center" style={{ border: `2px solid ${isSel ? themeColor : "#fff"}`, background: isSel ? themeColor : "rgba(21,22,30,0.4)" }}>
                      {isSel && <X size={0} />}
                      {isSel && <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={themeOn} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectMode && (
        <div className="fixed bottom-0 left-0 right-0 flex items-center justify-between px-4 py-3" style={{ background: colors.surfaceSecondary, borderTop: `1px solid ${colors.border}` }}>
          <span className="font-display font-medium text-sm" style={{ color: colors.onSurfaceSecondary }}>{selected.size} selected</span>
          <button data-testid="gallery-download" onClick={onDownload} disabled={selected.size === 0 || downloading} className="flex items-center gap-2 py-3 px-5 rounded-xl font-display font-bold text-[14.5px]" style={{ background: themeColor, color: themeOn, opacity: selected.size === 0 || downloading ? 0.4 : 1 }}>
            {downloading ? <Spinner style={{ width: 16, height: 16, borderTopColor: themeOn }} /> : <><Download size={18} /> Download</>}
          </button>
        </div>
      )}

      {viewer && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "rgba(8,8,12,0.97)" }}>
          <div className="absolute inset-0" onClick={() => setViewerIndex(null)} />
          <div className="relative flex items-center px-4 pt-4 pb-2">
            <button data-testid="viewer-close" onClick={() => setViewerIndex(null)} className="p-2 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }}><X size={24} color="#fff" /></button>
            <div className="flex-1" />
            {viewer.can_delete && (
              <button data-testid="viewer-delete" onClick={onDeleteViewer} className="p-2 rounded-full" style={{ background: "rgba(255,255,255,0.12)" }}><Trash2 size={22} color="#fff" /></button>
            )}
          </div>
          <img src={mediaUrl(viewer.url)} alt="" className="relative flex-1 w-full object-contain min-h-0" onClick={() => setViewerIndex(null)} />
          <div className="relative flex items-center gap-2 px-4 py-4">
            <Avatar name={viewer.uploader?.display_name} uri={viewer.uploader?.profile_photo_url} size={30} />
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-sm text-white truncate">{viewer.uploader?.display_name || "Someone"}</p>
              <p className="font-display text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>
                {mode === "global" && viewer.group?.name ? `${viewer.group.name} · ` : ""}{photoDate(viewer.created_at)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
